import * as PIXI from "pixi";
import { Mc } from "../piou-animate";
import Cs from "../utils/Cs";
import Timer from "../utils/Timer";
import Bouncer from "./Bouncer";
import Game from "./Game";
import Phys from "./Phys";
import Part from "./Part";
import Action from "./actions/Action";
import { ActionType } from "./actions";
import Loader from "../Loader";

export enum PiouStep {
  FLY = 0,
  WALK = 1,
  FALL = 2,
  EXIT = 3,
  ACTION = 10,
}

export enum PiouSuperPower {
  CLIMBER = 0,
  PSIONIC = 1,
  STUNTMAN = 2,
  RUNNER = 3,
}

export default class Piou extends Phys<{ sub: Mc }> {
  // TODO: annotate Piou fields maybe split
  static RAY = 6;
  static CLIMB = 6;
  static SPEED = 0.7;
  static ORIENT = 20;
  static BREAK = 0.25;
  static WEIGHT = 0.3;

  static FALL_BOUNCE_LIMIT = 4;
  static FALL_DEATH_LIMIT = 7;

  static WALK_FRAME = 16;

  sPower: PiouSuperPower | null = null;

  flDeath: boolean = false;
  flOut: boolean;

  speed: number;
  frame: number;

  sens: number;
  step: number | null = null;
  px: number = 0;
  py: number = 0;
  gid: number;
  noSelection: number | null = null;

  parc: number = 0;
  outTest: number;

  angle: number = 0;
  coefAngle: number = 0;

  exitSpot: { x: number; y: number } | null = null;
  exitMask: PIXI.Graphics | null = null;
  currentAction: Action | null = null;

  get centerPosition() {
    return new PIXI.Point(this.x, this.y - 6);
  }

  constructor(game: Game, x: number, y: number) {
    const mcPiou = new Loader.movieclips.mcPiou();
    mcPiou.name = "McPiou";
    super(game, mcPiou);
    this.x = x;
    this.y = y;
    // this.updatePos();
    this.root.stop();

    this.weight = 0.3;
    this.speed = Piou.SPEED;
    this.frict = 0.97;

    this.flOut = true;

    this.outTest = 0;
    this.sens = 1;
    this.gid = 1;
    this.initStep(PiouStep.FLY);
    this.frame = 0;

    this.game.piouCount += 1;
  }

  initStep(n: PiouStep) {
    switch (this.step) {
      case PiouStep.FLY:
        this.bouncer?.removeAllListeners();
        this.bouncer = null;
        this.weight = 0;
        this.root.sub.rotation = 0;
        break;
      case PiouStep.WALK:
        this.root.rotation = 0;
        break;
      case PiouStep.FALL:
        this.bouncer?.removeAllListeners();
        this.bouncer = null;
        this.weight = 0;
        break;
      case PiouStep.EXIT:
        console.error("UNDEAD ERROR !");
    }

    this.step = n;
    switch (this.step) {
      case PiouStep.FLY:
        this.root.gotoAndStop("fly");
        this.vx = 0;
        this.vy = 0;
        this.weight = Piou.WEIGHT;
        this.bouncer = new Bouncer(this);
        this.bouncer.on("bounceGround", () => {
          this.land(false);
        });
        this.root.sub.angle = Math.random() * 360;
        break;

      case PiouStep.WALK:
        this.initWalkState();
        this.root.gotoAndStop("walk");
        break;

      case PiouStep.FALL:
        this.vx = 0;
        this.vy = 1;
        this.weight = Piou.WEIGHT;
        this.bouncer = new Bouncer(this);
        this.bouncer.on("bounce", () => this.land(true));
        break;

      case PiouStep.EXIT:
        this.gid = 0;
        this.root.gotoAndStop("fly");
        this.root.x = this.x;
        this.root.y = this.y;
        this.coefAngle = 0.1;
        this.root.sub.angle = Math.random() * 360;
        this.angle = Math.atan2(this.vy, this.vx);
        this.flOut = false;

        this.game.piouCount -= 1;
        this.game.piouGoal -= 1;

        // Cs.game.piouOut--;
        // Cs.game.piouIn++;
        // Cs.game.endTime = Cs.game.btimer * 32; //Std.getTimer()- Cs.game.startTime
        // Inter.freezeTimer();
        // Inter.updateScorePanel();
        // Cs.game.pList.remove(this);
        // Cs.game.deathList.push(this);
        this.updateColor(this.root);

        break;
    }
  }

  initWalkState() {
    this.parc = 0;
    this.vx = 0;
    this.vy = 0;
    this.px = Math.floor(this.x);
    this.py = Math.floor(this.y);
    let cl = 0;

    while (this.game.level.isFree(this.px, this.py + 1)) {
      this.py++;
      if (cl++ > Piou.CLIMB) break;
    }
    this.y = this.py;
  }

  reverse() {
    this.sens *= -1;
    this.root.scale.x = this.sens;
  }

  update() {
    super.update();
    switch (this.step) {
      case PiouStep.FLY:
        if (this.currentAction === null) {
          this.root.sub.angle += 8 * Timer.tmod;
        }
        this.checkExit();
        this.checkLim();
        break;

      case PiouStep.WALK:
        if (this.sPower === PiouSuperPower.CLIMBER) {
          this.climb();
        } else {
          this.walk();
        }

        this.frame = (this.frame + this.speed * 1.5) % Piou.WALK_FRAME;
        this.root.sub.gotoAndStop(Math.floor(this.frame));

        this.checkExit();
        this.checkLim();
        break;

      case PiouStep.FALL:
        this.checkExit();
        this.checkLim();
        break;

      case PiouStep.EXIT:
        this.root.sub.angle += 8 * Timer.tmod;
        this.exitFly();
        break;

      case PiouStep.ACTION:
        this.checkExit();
        this.checkLim();
        break;
    }
    // NO SELECTION => Disable piou selection for x frames
    if (this.noSelection != null) {
      this.noSelection--;
      if (this.noSelection == 0) this.noSelection = null;
    }

    // CAISSE
    // for (let i = 0; i < Cs.game.cList.length; i++) {
    //   let c = Cs.game.cList[i];
    //   if (this.getDist(c) < 14) {
    //     c.activate();
    //   }
    // }
  }

  checkLim() {
    let rad = 1;
    if (this.x < 0) this.explode(0, rad);
    if (this.x > this.game.level.lvlWidth) this.explode(3.14, rad);
    if (this.y < 0) this.explode(1.57, rad);
    if (this.y > this.game.level.lvlHeight) this.explode(-1.57, rad);
  }

  checkExit() {
    this.outTest -= Timer.tmod;

    if (this.outTest < 0) {
      this.outTest = 200;
      for (const out of this.game.lvlInfo.data.out) {
        let p = { x: out[0], y: out[1] };
        let dist = this.getDist(p);
        this.outTest = Math.min((dist - 50) * 0.2, this.outTest);

        if (dist < 30 && this.currentAction?.flExclu != true) {
          if (this.currentAction != null) this.currentAction.interrupt();
          this.exitSpot = p;
          if (this.step == PiouStep.WALK) {
            this.vx = this.sens;
            this.vy = 0;
          }
          this.initStep(PiouStep.EXIT);
          return;
        } else {
          if (this.step != PiouStep.WALK) this.outTest *= 0.25;
        }
      }
    }
  }

  walk() {
    this.parc += this.speed;

    while (this.parc > 1) {
      let rot = 0;
      this.parc--;
      this.px += this.sens;
      if (!this.game.level.isFree(this.px, this.py)) {
        let flReturn = true;
        for (let by = 1; by <= Piou.CLIMB; by++) {
          if (this.game.level.isFree(this.px, this.py - by)) {
            this.py -= by;
            flReturn = false;
            rot = -by * Piou.ORIENT * this.sens;
            this.parc -= by * Piou.BREAK;
            break;
          }
        }
        if (flReturn) {
          this.reverse();
          this.px += this.sens;
          if (!this.game.level.isFree(this.px, this.py)) this.py--;
        }
      } else {
        if (this.game.level.isFree(this.px, this.py + 1)) {
          let flFall = true;
          for (let by = 1; by <= Piou.CLIMB; by++) {
            if (!this.game.level.isFree(this.px, this.py + by + 1)) {
              this.py += by;
              flFall = false;
              rot = by * Piou.ORIENT * this.sens;
              this.parc -= by * Piou.BREAK;
              break;
            }
          }
          if (flFall) {
            if (
              this.sPower == PiouSuperPower.RUNNER &&
              this.game.level.isFree(this.px, this.py - 1)
            ) {
              this.jump();
            } else {
              this.x = this.px;
              this.y = this.py;
              this.fall();
            }
          }
        }
      }
    }
    this.x = this.px;
    this.y = this.py;
  }

  jump() {
    // var ac = new ac_Piou_Jump(this.x, this.y);
    // downcast(ac).piou = this;
    // ac.init();
    // this.vx *= 0.6;
    // this.vy *= 0.5;
  }

  climb() {
    let trot = (this.gid - 1) * 90;
    this.parc += this.speed * Timer.tmod;

    if (this.parc > 0 && !this.game.level.isFree(this.px, this.py)) {
      this.parc = 0;
      this.py--;
    }

    while (this.parc > 1) {
      this.parc--;

      let fwd = Cs.DIR[Cs.sMod(this.gid - this.sens, 4)];
      let sky = Cs.DIR[Cs.sMod(this.gid + 2, 4)];
      let grd = Cs.DIR[this.gid];

      this.px += fwd[0];
      this.py += fwd[1];

      let clv = Piou.CLIMB;
      if (this.gid != 1) clv *= 0.5;

      if (!this.game.level.isFree(this.px, this.py)) {
        let flReturn = true;
        for (let by = 1; by <= clv; by++) {
          if (
            this.game.level.isFree(this.px + sky[0] * by, this.py + sky[1] * by)
          ) {
            this.px += sky[0] * by;
            this.py += sky[1] * by;
            flReturn = false;
            this.parc -= by * Piou.BREAK;
            //trot -= ORIENT*by*sens
            break;
          }
        }
        if (flReturn) {
          this.gid = Cs.sMod(this.gid - this.sens, 4);
          this.px -= fwd[0];
          this.py -= fwd[1];
          if (this.gid == 3) {
            this.gid = 1;
            this.reverse();
            this.x = this.px;
            this.y = this.py;
            this.fall();
          }

          //if(!Level.isFree(px,py))py--;
        }
      } else {
        if (this.game.level.isFree(this.px + grd[0], this.py + grd[1])) {
          let flFall = true;
          for (let by = 1; by <= clv; by++) {
            if (
              !this.game.level.isFree(
                this.px + grd[0] * by,
                this.py + grd[1] * by + 1
              )
            ) {
              this.px += grd[0] * by;
              this.py += grd[1] * by;
              flFall = false;
              this.parc -= by * Piou.BREAK;
              //trot += ORIENT*by*sens
              break;
            }
          }
          if (flFall) {
            this.x = this.px;
            this.y = this.py;
            if (this.gid == 1) {
              this.fall();
            } else {
              this.gid = Cs.sMod(this.gid + this.sens, 4);
            }
          }
        }
      }
    }
    this.x = this.px;
    this.y = this.py;

    let drot = Cs.hMod(trot - this.root.angle, 180);
    this.root.angle += drot * 0.3;
  }

  exitFly() {
    if (!this.exitSpot) return;

    if (this.sens === -1) {
      this.reverse(); // Fix negative x scale issue
    }

    let sp = 0.5;
    let dx = this.exitSpot.x - this.x;
    let dy = this.exitSpot.y - this.y;
    let ta = Math.atan2(dy, dx);
    let da = Cs.hMod(ta - this.angle, 3.14);
    this.coefAngle = Math.min(this.coefAngle + 0.01, 1);
    this.angle += da * this.coefAngle;
    this.vx += Math.cos(this.angle) * sp;
    this.vy += Math.sin(this.angle) * sp;
    this.frict = 0.95;
    let dist = Math.sqrt(dx * dx + dy * dy);
    let scale = Math.min(dist * 8, this.root.scale.x * 100);
    let ds = scale - this.root.scale.x * 100;
    this.root.scale.set(this.root.scale.x + (ds * 0.3) / 100);
    if (scale < 10) {
      this.root.mask = null;
      if (this.exitMask) {
        this.game.removeChild(this.exitMask);
        this.exitMask = null;
      }
      // Cs.game.deathList.remove(this);
      this.kill();
      return;
    }
    if (this.exitMask == null && dist < 18) {
      const gr = new PIXI.Graphics();
      gr.beginFill(0xff0000).drawCircle(0, 0, 24).endFill();
      this.exitMask = gr;

      this.exitMask.name = "exitMask";
      this.exitMask.x = this.exitSpot.x;
      this.exitMask.y = this.exitSpot.y;
      this.root.mask = this.exitMask;
      this.game.addChild(this.exitMask);
    }
  }

  land(flControl: boolean) {
    let pw = Math.sqrt(this.vx * this.vx + this.vy * this.vy);

    if (pw < Piou.FALL_BOUNCE_LIMIT) {
      this.initStep(PiouStep.WALK);
    } else if (pw < Piou.FALL_DEATH_LIMIT) {
      if (flControl || this.sPower == PiouSuperPower.STUNTMAN) {
        this.initStep(PiouStep.WALK);
      }
    } else {
      if (this.sPower == PiouSuperPower.STUNTMAN) {
        this.smoke();
        this.initStep(PiouStep.WALK);
      } else {
        // partSplatch is removed in framescript
        const p = new Part(this.game, "partSplatch");
        p.setScale(42);
        p.x = this.x;
        p.y = this.y + 3;
        p.updatePos();
        this.explode(-1.57, 1.57);

        // TODO: remove that !!
        p.kill();
      }
    }
  }

  smoke() {
    for (let i = 0; i < 5; i++) {
      const dx = (Math.random() * 2 - 1) * 14;
      const p = new Part(this.game, "mcNuage");
      p.x = this.x + dx;
      p.y = this.y;
      p.setScale(150 - Math.abs(dx) * 5);
      p.vy = -Math.random();
      p.vr = dx;
    }
  }

  initWalk() {
    this.initStep(PiouStep.WALK);
  }

  fall() {
    this.initStep(PiouStep.FALL);
    this.root.gotoAndStop("fall");
  }

  setSupaPowa(n: number) {
    switch (this.sPower) {
      case PiouSuperPower.CLIMBER:
        if (this.gid != 1) {
          this.fall();
          this.gid = 1;
        }
        this.root.rotation = 0;
        break;
      case PiouSuperPower.RUNNER:
        this.speed = Piou.SPEED;
        break;
    }
    this.sPower = n;
    switch (this.sPower) {
      case PiouSuperPower.RUNNER:
        this.speed = 2;
        break;
    }

    this.updateColor(this.root);

    // PART
    for (let i = 0; i < 12; i++) {
      let p = new Part(this.game, "partPaint");
      let a = Math.random() * 6.28;
      let ca = Math.cos(a);
      let sa = Math.sin(a);
      let ray = 2 + Math.random() * 8;
      let speed = Math.random() * 0.5;
      p.x = this.x + ca * ray;
      p.y = this.y + sa * ray - Piou.RAY;
      p.vx = ca * speed;
      p.vy = sa * speed;
      p.setScale(30 + Math.random() * 100);
      Cs.setColorMatrix(p.root, Cs.PIOU_COLOR_MATRIX[this.sPower], -50);
      p.timer = 10 + Math.random() * 10;
      // TODO: apply correctly blend mode
      // p.root.blendMode = PIXI.BLEND_MODES.ADD;
    }
  }

  updateColor(mc: PIXI.animate.DisplayObject) {
    if (this.sPower)
      Cs.setColorMatrix(mc, Cs.PIOU_COLOR_MATRIX[this.sPower], null);
  }

  die() {
    this.flDeath = true;
    this.kill();
  }

  explode(ba: number, ra: number) {
    this.gerb(ba, ra, 18, 6);
    this.die();
  }

  gerb(ba: number, ra: number, max: number, power: number) {
    let ray = 4;
    let lim = this.game.sprites.length;
    if (lim > 40) {
      max *= 40 / lim;
    }

    for (let i = 0; i < max; i++) {
      let a = ba + (Math.random() * 2 - 1) * ra;
      let ca = Math.cos(a);
      let sa = Math.sin(a);
      let sp = 0.5 + Math.random() * power;
      let p = this.getGib(this.x + ca * ray, this.y + sa * ray);
      p.vx = ca * sp;
      p.vy = sa * sp;
      p.root.filters = this.root.filters;
    }
  }

  getGib(px: number, py: number) {
    const p = new Part(this.game, "mcDebris");
    p.x = px;
    p.y = py;
    p.weight = 0.1 + Math.random() * 0.1;
    p.setScale(40 + Math.random() * 80);
    p.timer = 10 + Math.random() * 30;
    p.fadeType = 0;
    Cs.setColor(p.root, 0xffc1c1, -255);
    p.bouncer = new Bouncer(p);
    if (!this.game.level.isFree(Math.floor(p.x), Math.floor(p.y))) {
      p.kill();
    }
    return p;
  }

  isSelectable(action: ActionType) {
    // Allow only when piou is on the ground, or in an action
    if (this.noSelection !== null) return false;
    if (this.gid !== 1) return false;
    if (this.step !== PiouStep.WALK && this.step !== PiouStep.ACTION)
      return false;
    if (this.currentAction) {
      if (this.currentAction.flExclu) return false;
      if (this.currentAction.id === action) {
        return this.currentAction.flSameIsOk;
      }
    }
    return true;
  }

  kill() {
    if (this.flOut) this.game.piouCount -= 1;
    // Inter.updateScorePanel();
    // Cs.game.pList.remove(this);
    super.kill();
  }
}
